import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import type { V4SiteListItem } from '../../api';

interface SiteComboboxProps {
  /** Every site on the customer — never pre-filtered by tag. */
  sites: V4SiteListItem[];
  /**
   * Tag whose sites are listed first, as suggestions. Sites without it stay
   * reachable in a second group: the tag decides ORDER, never visibility.
   */
  suggestedTag: 'billing' | 'shipping';
  /** Selected site id, or '' when none is picked. */
  value: string;
  onSelect: (siteId: string) => void;
  placeholder: string;
  disabled?: boolean;
  testId: string;
}

/**
 * Searchable address picker for the quote form.
 *
 * Replaces the plain `<Select>`s that listed only tag-matching sites, which
 * made an untagged customer address unreachable and could leave the billing
 * list completely empty (issue #1200). Mirrors the customer/manager/ticket
 * comboboxes already in QuoteFormModal: cmdk does the filtering, matching on
 * each item's `value`, which carries the street and city as well as the name.
 */
export function SiteCombobox({
  sites, suggestedTag, value, onSelect, placeholder, disabled, testId,
}: SiteComboboxProps) {
  const { t } = useTranslation('quotes');
  const [open, setOpen] = useState(false);

  const [suggested, others] = useMemo(() => [
    sites.filter((site) => site.tags?.includes(suggestedTag)),
    sites.filter((site) => !site.tags?.includes(suggestedTag)),
  ], [sites, suggestedTag]);

  // Resolved from every site, not just the suggested ones, so a quote whose
  // saved address lost its tag still shows that address on the trigger.
  // Site identity is the Mongo id — what the quote payload stores.
  const selected = sites.find((site) => site.mongoSiteId === value);

  const pick = (siteId: string) => {
    onSelect(siteId);
    setOpen(false);
  };

  const renderItem = (site: V4SiteListItem) => (
    <CommandItem
      key={site.mongoSiteId}
      value={[site.name, site.addressLine1, site.city, site.postalCode].filter(Boolean).join(' ')}
      onSelect={() => pick(site.mongoSiteId)}
    >
      <Check className={cn('mr-2 h-4 w-4', site.mongoSiteId === value ? 'opacity-100' : 'opacity-0')} />
      {site.name}
    </CommandItem>
  );

  return (
    <div data-testid={testId} className="flex-1 min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn('w-full justify-between font-normal', !selected && 'text-muted-foreground')}
          >
            <span className="truncate">{selected ? selected.name : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('fields.searchAddresses', 'Search addresses')} />
            <CommandList>
              <CommandEmpty>{t('fields.noAddressesFound', 'No address found')}</CommandEmpty>
              <CommandGroup>
                <CommandItem value={t('fields.none')} onSelect={() => pick('')}>
                  <Check className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                  {t('fields.none')}
                </CommandItem>
              </CommandGroup>
              {suggested.length > 0 && (
                <CommandGroup heading={t('fields.suggestedAddresses', 'Suggested')}>
                  {suggested.map(renderItem)}
                </CommandGroup>
              )}
              {others.length > 0 && (
                <CommandGroup heading={t('fields.otherAddresses', 'Other addresses')}>
                  {others.map(renderItem)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
